import {type ExtendedItemProps, ItemPicker} from '@github-ui/item-picker-base/ItemPicker'
import type {ItemGroup} from '@github-ui/item-picker-base/shared'
import {useCallback, useMemo, useState} from 'react'
import {useRelayEnvironment} from 'react-relay'

import {LABELS} from '../../../constants/labels'
import {commitUpdateProjectItemFieldValueMutation} from '../../../mutations/update-project-item-field-value'
import type {
  ProjectIterationFieldNode,
  ProjectIterationFieldValueNode,
} from '../project-item-section-fields-query-types'
import {FieldWrapper} from './FieldWrapper'
import {IterationItem} from './IterationItem'
import type {ClearableFieldProps} from './Shared'

type IterationFieldIteration = ProjectIterationFieldNode['configuration']['iterations'][number]

export type IterationFieldFragment = Pick<
  ProjectIterationFieldValueNode,
  'iterationId' | 'title' | 'titleHTML' | 'startDate' | 'duration'
>

const completedGroup: ItemGroup = {groupId: 'completed', header: {title: 'Completed', variant: 'filled'}}
const activeGroup: ItemGroup = {groupId: 'active', header: {title: 'Active', variant: 'filled'}}

type IterationFieldProps = ClearableFieldProps<
  Pick<ProjectIterationFieldNode, 'id' | 'name' | 'configuration'>,
  IterationFieldFragment
>

export function IterationField({
  viewerCanUpdate,
  itemId,
  projectId,
  field,
  value: valueData,
  onIssueUpdate,
  onClearFieldValue,
}: IterationFieldProps) {
  const [showInput, setShowInput] = useState(false)
  const environment = useRelayEnvironment()

  const fieldId = field.id
  const fieldName = field.name

  const [filter, setFilter] = useState('')

  const onSelectionChange = useCallback(
    (selection: IterationFieldIteration[]) => {
      const selectedIteration = selection[0]
      if (selection.length === 1 && selectedIteration) {
        commitUpdateProjectItemFieldValueMutation({
          environment,
          input: {
            fieldId,
            itemId,
            projectId,
            value: {iterationId: selectedIteration.id},
          },
          optimisticFieldValue: {
            id: `optimistic-field-value-${itemId}-${fieldId}`,
            field: {id: fieldId},
            iterationId: selectedIteration.id,
            title: selectedIteration.title,
            titleHTML: selectedIteration.titleHTML,
            startDate: selectedIteration.startDate,
            duration: selectedIteration.duration,
          },
          onCompleted: onIssueUpdate,
        })
      } else if (selection.length === 0) {
        onClearFieldValue(fieldId)
      }
      setShowInput(false)
    },
    [environment, fieldId, itemId, projectId, onClearFieldValue, onIssueUpdate],
  )

  const filterItems = useCallback((v: string) => {
    setFilter(v)
  }, [])

  const items = useMemo(() => {
    const iterations = (field.configuration?.iterations || []).concat(field.configuration?.completedIterations || [])

    if (!filter) return iterations
    return iterations.filter(l => l.title.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [field.configuration?.completedIterations, field.configuration?.iterations, filter])

  const completedIds = useMemo(() => {
    const iterations = field.configuration?.completedIterations || []
    return new Set(iterations.map(i => i.id))
  }, [field.configuration?.completedIterations])

  const selectedItems = useMemo(() => {
    const activeIterations = field.configuration?.iterations || []
    const completedIterations = field.configuration?.completedIterations || []
    const iterations = activeIterations.concat(completedIterations)

    return iterations.filter(i => i.id === valueData?.iterationId)
  }, [field.configuration?.completedIterations, field.configuration?.iterations, valueData?.iterationId])

  const convertToItemProps = useCallback(
    (option: IterationFieldIteration): ExtendedItemProps<IterationFieldIteration> => {
      return {
        id: option.id,
        children: (
          <IterationItem startDate={option.startDate} durationInDays={option.duration} titleHTML={option.titleHTML} />
        ),
        source: option,
        groupId: completedIds.has(option.id) ? completedGroup.groupId : activeGroup.groupId,
      }
    },
    [completedIds],
  )

  const renderAnchor = useCallback(
    (anchorProps: React.HTMLAttributes<HTMLElement>) => {
      return (
        <FieldWrapper
          showInput={showInput}
          setShowInput={setShowInput}
          anchorProps={anchorProps}
          placeholder={LABELS.emptySections.iterationPlaceholder}
          value={
            valueData ? (
              <IterationItem
                startDate={valueData.startDate}
                durationInDays={valueData.duration}
                titleHTML={valueData.titleHTML}
              />
            ) : null
          }
          name={fieldName}
        />
      )
    },
    [fieldName, showInput, valueData],
  )

  const groups = useMemo(() => {
    return completedIds.size > 0 ? [activeGroup, completedGroup] : [activeGroup]
  }, [completedIds.size])

  if (!viewerCanUpdate)
    return (
      <FieldWrapper
        showInput={showInput}
        setShowInput={setShowInput}
        value={
          valueData ? (
            <IterationItem
              startDate={valueData.startDate}
              durationInDays={valueData.duration}
              titleHTML={valueData.titleHTML}
            />
          ) : null
        }
        placeholder={LABELS.emptySections.noValue(fieldName)}
        canUpdate={false}
        name={fieldName}
      />
    )

  return (
    <ItemPicker
      items={items}
      groups={groups}
      initialSelectedItems={selectedItems}
      filterItems={filterItems}
      getItemKey={i => i.id}
      convertToItemProps={convertToItemProps}
      placeholderText={LABELS.emptySections.iterationFilterPlaceholder}
      selectionVariant="single"
      onSelectionChange={onSelectionChange}
      renderAnchor={renderAnchor}
      height={'large'}
      cssAnchorPositioningSettings={{fallbackStrategy: 'opposite-side'}}
    />
  )
}
